import { useEffect, useState } from "react";
import { useQuery, type UseQueryOptions, type UseQueryResult } from "@tanstack/react-query";

import { isUnauthorizedError } from "@/lib/auth-failure";

type UseCancellingQueryOptions<TData = unknown, TError = Error> = {
	retryAttempts?: number;
} & Omit<UseQueryOptions<TData, TError>, "retry">;

type UseCancellingQueryReturn<TData = unknown, TError = Error> = {
	refetchEnabled: boolean;
} & Omit<UseQueryResult<TData, TError>, "failureCount">;

export function useCancellingQuery<TData = unknown, TError = Error>({
	retryAttempts = 3,
	refetchInterval,
	...queryOptions
}: UseCancellingQueryOptions<TData, TError>): UseCancellingQueryReturn<TData, TError> {
	const [refetchEnabled, setRefetchEnabled] = useState(true);
	const queryResult = useQuery({
		...queryOptions,
		refetchInterval: refetchEnabled ? refetchInterval : false,
		retry: (failureCount, error) => {
			if (isUnauthorizedError(error)) {
				return false;
			}

			return failureCount <= retryAttempts;
		},
	});

	const { failureCount, ...rest } = queryResult;

	useEffect(() => {
		if ((isUnauthorizedError(queryResult.error) || failureCount > retryAttempts) && refetchEnabled) {
			setRefetchEnabled(false);
		}
	}, [queryResult.error, failureCount, retryAttempts, refetchEnabled]);

	return { refetchEnabled, ...rest };
}
